// src/lib/maps/ensureGoogleMapsLoaded.js

/**
 * ✅ Ensures Google Maps JavaScript API is loaded exactly once
 * Handles multiple concurrent calls gracefully
 */

let loadPromise = null;
let isLoaded = false;

export const ensureGoogleMapsLoaded = ({ apiKey, libraries = [] }) => {
  // Already loaded
  if (isLoaded && window.google?.maps) {
    console.log("[ensureGoogleMapsLoaded] ✅ Already loaded");
    return Promise.resolve();
  }

  // Currently loading - return existing promise
  if (loadPromise) {
    console.log("[ensureGoogleMapsLoaded] ⏳ Already loading, returning existing promise");
    return loadPromise;
  }

  // Start new load
  console.log("[ensureGoogleMapsLoaded] 🚀 Starting to load Google Maps API...");
  
  loadPromise = new Promise((resolve, reject) => {
    // Validate API key
    if (!apiKey || typeof apiKey !== "string") {
      const error = new Error("Google Maps API key is required");
      console.error("[ensureGoogleMapsLoaded] ❌", error.message);
      reject(error);
      return;
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );

    if (existingScript) {
      console.log("[ensureGoogleMapsLoaded] 📜 Script tag exists, waiting for window.google...");
      
      // Script exists but may not be loaded yet
      const checkLoaded = () => {
        if (window.google?.maps) {
          console.log("[ensureGoogleMapsLoaded] ✅ Google Maps loaded via existing script");
          isLoaded = true;
          resolve();
        } else {
          setTimeout(checkLoaded, 50);
        }
      };
      
      checkLoaded();
      return;
    }

    // Create new script tag
    const script = document.createElement("script");
    
    // Build URL with parameters
    const params = new URLSearchParams({
      key: apiKey,
      callback: "__googleMapsLoaded__", // Custom callback
    });

    if (libraries.length > 0) {
      params.append("libraries", libraries.join(","));
    }

    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;

    // ✅ Set up callback BEFORE adding script to DOM
    window.__googleMapsLoaded__ = () => {
      console.log("[ensureGoogleMapsLoaded] ✅ Callback fired - Google Maps loaded successfully");
      isLoaded = true;
      delete window.__googleMapsLoaded__; // Clean up
      resolve();
    };

    script.onerror = (event) => {
      const error = new Error(
        "Failed to load Google Maps script. Check your API key and network connection."
      );
      console.error("[ensureGoogleMapsLoaded] ❌ Script error:", error.message, event);
      delete window.__googleMapsLoaded__;
      loadPromise = null;
      reject(error);
    };

    // Add script to DOM
    document.head.appendChild(script);
    console.log("[ensureGoogleMapsLoaded] 📜 Script tag added to DOM:", script.src);
  });

  return loadPromise;
};

/**
 * Reset the loader state (useful for testing or hot reload)
 */
export const resetGoogleMapsLoader = () => {
  loadPromise = null;
  isLoaded = false;
  console.log("[ensureGoogleMapsLoaded] 🔄 Loader reset");
};